/*
 * Copyright (c) CIRROM 2025.
 */

import React, { useEffect, useState } from "react";
import {
  Box,
  CardHeader,
  Avatar,
  Chip,
  CircularProgress,
  IconButton,
  Link,
} from "@mui/material";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import styled from "@emotion/styled";
import { MiraCard, MiraCardContent } from "../mira/components/common/Card";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { SuggestedMonitorDetailsModal } from "../cirrom-monitor/SuggestedMonitorDetailsModal";
import { RecordReviewModal } from "../record-review/RecordReviewModal";
import { displayCombinedRecordModal } from "../../utils/uiPreference";
import { usePagination } from "../../hooks/usePagination";
import { MiraTheme } from "../mira/theme/theme";

const MoverCard = styled(Box)<{ theme?: MiraTheme }>`
  display: flex;
  align-items: center;
  padding: ${(props) => props.theme.spacing(1.5)};
  border-radius: ${(props) => props.theme.shape.borderRadius}px;
  background: ${(props) =>
    props.theme.palette.mode === "dark"
      ? props.theme.palette.background.default
      : props.theme.palette.grey[50]};
  margin-bottom: ${(props) => props.theme.spacing(1)};
  transition: all 0.3s ease;
  border: 1px solid ${(props) => props.theme.palette.divider};
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: ${(props) => props.theme.palette.primary.main};
  }
`;

export interface RecentMoversDataItem {
  record_id: number;
  name: string;
  date?: string;
}

export interface RecentMoversPanelConfig<T extends RecentMoversDataItem> {
  title: string;
  subheader: string;
  emptyMessage: string;
  icon: React.ElementType;
  avatarColor: string;
  trendingColor: "success" | "info" | "warning" | "error";
  fetchFunction: (
    projectId: number | null,
    systemId: number | null
  ) => Promise<T[] | T>;
  getOldValue: (item: T) => string;
  getNewValue: (item: T) => string;
  seeMoreTab: string;
}

interface RecentMoversPanelProps<T extends RecentMoversDataItem> {
  isDataProject: boolean;
  dataProjectId: number;
  integratedSystemId: number;
  config: RecentMoversPanelConfig<T>;
  itemsPerPage?: number;
}

export function RecentMoversPanel<T extends RecentMoversDataItem>({
  isDataProject,
  dataProjectId,
  integratedSystemId,
  config,
  itemsPerPage = 5,
}: RecentMoversPanelProps<T>) {
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null
  );
  /*
   * Captured when the mover is opened rather than read on every render, so
   * toggling the preference from the navbar cannot swap the surface out from
   * under a steward who is mid-decision.
   */
  const [useCombinedModal, setUseCombinedModal] = useState<boolean>(false);

  const {
    currentPage,
    totalPages,
    displayedItems,
    handlePrevious,
    handleNext,
  } = usePagination({ items: data, itemsPerPage });

  useEffect(() => {
    if (integratedSystemId) {
      setIsLoading(true);
      config
        .fetchFunction(
          isDataProject ? dataProjectId : undefined,
          !isDataProject ? integratedSystemId : undefined
        )
        .then((response) => {
          setData(Array.isArray(response) ? response : []);
        })
        .finally(() => setIsLoading(false));
    }
  }, [integratedSystemId, isDataProject, config.fetchFunction]);

  const handleItemClick = (recordId: number) => {
    setSelectedContactId(recordId);
    setUseCombinedModal(displayCombinedRecordModal());
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedContactId(null);
  };

  const IconComponent = config.icon;

  if (isLoading) {
    return (
      <MiraCard style={{ height: "100%" }}>
        <CardHeader title={config.title} />
        <MiraCardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="200px"
          >
            <CircularProgress />
          </Box>
        </MiraCardContent>
      </MiraCard>
    );
  }

  return (
    <MiraCard style={{ height: "100%" }}>
      <CardHeader title={config.title} subheader={config.subheader} />
      <MiraCardContent>
        {data.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="textSecondary">
              {config.emptyMessage}
            </Typography>
          </Box>
        ) : (
          <>
            <Box>
              {displayedItems.map((item) => (
                <MoverCard
                  key={item.record_id}
                  onClick={() => handleItemClick(item.record_id)}
                >
                  <Avatar
                    sx={{
                      bgcolor: config.avatarColor,
                      width: 28,
                      height: 28,
                      mr: 1.5,
                    }}
                  >
                    <IconComponent sx={{ fontSize: 16 }} />
                  </Avatar>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight={600}>
                        {item.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {config.getOldValue(item)}
                      </Typography>
                      <TrendingUpIcon
                        sx={{ fontSize: 14 }}
                        color={config.trendingColor}
                      />
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="primary"
                      >
                        {config.getNewValue(item)}
                      </Typography>
                      {item.date && (
                        <Chip
                          label={item.date}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      )}
                    </Box>
                  </Box>
                </MoverCard>
              ))}
            </Box>
            {totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                gap={1}
                mt={2}
              >
                <Typography variant="caption" color="textSecondary">
                  {currentPage + 1} of {totalPages}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handlePrevious}
                  disabled={currentPage === 0}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleNext}
                  disabled={currentPage === totalPages - 1}
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Link
                component={RouterLink}
                to={`${
                  isDataProject ? "/data-project" : "/product"
                }/contact-monitor/company/${
                  isDataProject ? dataProjectId : integratedSystemId
                }?tab=${config.seeMoreTab}`}
                variant="body2"
                underline="hover"
              >
                See more
              </Link>
            </Box>
          </>
        )}
      </MiraCardContent>
      {/*
       * Two review surfaces, one preference, as on the suggested changes grid
       * and the monitor linkage page: the combined record modal is the newer
       * layout, and without the flag this behaves as before. Both take the
       * same record and system, so the panel does not care which one is up.
       */}
      {!!selectedContactId &&
        (useCombinedModal ? (
          <RecordReviewModal
            open={isModalOpen}
            onClose={handleModalClose}
            /*
             * Deliberately not closing here. The card settles several changes
             * in one sitting and reloads itself after each write, and
             * onSuccess also fires when the CRM refused the change - closing
             * would throw the steward out mid-record and take the error
             * message with it.
             */
            onSuccess={() => {}}
            recordId={selectedContactId}
            integratedSystemId={integratedSystemId}
          />
        ) : (
          <SuggestedMonitorDetailsModal
            open={isModalOpen}
            onClose={handleModalClose}
            onSuccess={handleModalClose}
            recordId={selectedContactId}
            integratedSystemId={integratedSystemId}
          />
        ))}
    </MiraCard>
  );
}
